import { useRoomUploadRoomPicture } from "@crowdcoursing/api/openapi";
import { useCallback, useState } from "react";

import { makeRoomAvatarUrl } from "~utils/url";

interface UseRoomAvatarResult {
  imageUrl: string | undefined;
  status: "idle" | "loading" | "error" | "success";
  uploadImage: (file: File) => Promise<void>;
}

export const useRoomAvatar = (roomId: string | undefined): UseRoomAvatarResult => {
  const [status, setStatus] = useState<UseRoomAvatarResult["status"]>("idle");
  const [uploadTimestamp, setUploadTimestamp] = useState<number>();

  const { mutateAsync: uploadRoomPicture } = useRoomUploadRoomPicture();

  // Get the avatar URL with proper URL construction
  const baseImageUrl = roomId ? makeRoomAvatarUrl(roomId) : undefined;
  const imageUrl = baseImageUrl && uploadTimestamp ? `${baseImageUrl}?t=${uploadTimestamp}` : baseImageUrl;

  const uploadImage = useCallback(
    async (file: File) => {
      if (!roomId) {
        setStatus("error");
        throw new Error("Cannot upload image without a room ID");
      }

      setStatus("loading");

      // Create a new FormData instance
      const formData = new FormData();
      formData.append("FormFile", file);

      await uploadRoomPicture(
        {
          // Cast FormData to the expected request body type
          body: formData as unknown as { FormFile?: Blob },
          headers: {
            // Remove Content-Type header to let the browser set it with the boundary
            "Content-Type": undefined,
          },
          pathParams: { roomId },
        },
        {
          onError: () => {
            setStatus("error");
          },
          onSuccess: () => {
            // Update timestamp to force a refresh of the image
            setUploadTimestamp(Date.now());
            setStatus("success");
          },
        },
      );
    },
    [roomId, uploadRoomPicture],
  );

  return {
    imageUrl,
    status,
    uploadImage,
  };
};
